// frontend/src/contexts/ThemeModeContext.js
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, useMediaQuery, alpha } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// Definições de paleta bem distintas para teste
const lightPalette = {
  mode: 'light',
  primary: { main: '#1976d2' }, // Azul
  secondary: { main: '#dc004e' }, // Rosa
  background: { default: '#fafafa', paper: '#ffffff' }, // Cinza claro e branco
  text: { primary: 'rgba(0,0,0,0.6)', secondary: '#555555' },
  info: { main: '#0288d1' },
  divider: 'rgba(0, 0, 0, 0.12)',
  sidebar: {listMenuItem:{ selectedBackground: '#d3e3fd', hoverBackground:'rgba(11,87,208,0.08)'}}
};

const darkPalette = {
  mode: 'dark',
  primary: { main: '#C5B8FF' }, // Azul claro
  secondary: { main: '#f48fb1' }, // Rosa claro
  background: { default: '#201a44', paper: '#271f49' }, // Cinza escuro e quase preto
  text: { primary: '#ffffff', secondary: '#bbbbbb' }, // Branco e cinza claro
  info: { main: '#64b5f6' },
  divider: 'rgba(255, 255, 255, 0.12)',
  sidebar: {listMenuItem:{ selectedBackground: 'rgba(11, 87, 208, 0.75)',  hoverBackground:'rgba(124,172,248,0.08)'}}
};


const ThemeModeContext = createContext({
  setThemeModeSetting: (mode) => {},
  modeSetting: 'dark',
  activePaletteMode: 'dark',
});

export const useThemeMode = () => useContext(ThemeModeContext);

export const CustomThemeProvider = ({ children }) => {
  const [userChoiceMode, setUserChoiceMode] = useState(() => {
    const storedMode = localStorage.getItem('themeMode');
    return storedMode || 'dark';
  });

  useEffect(() => {
    localStorage.setItem('themeMode', userChoiceMode);

  }, [userChoiceMode]);

  const systemPrefersDark = useMediaQuery('(prefers-color-scheme: dark)');

  const activePaletteMode = useMemo(() => {
    if (userChoiceMode === 'system') {
      return systemPrefersDark ? 'dark' : 'light';
    }
    return userChoiceMode; // 'light' ou 'dark'
  }, [userChoiceMode, systemPrefersDark]);
  
  const muiTheme = useMemo(() => {
    const selectedPalette = activePaletteMode === 'dark' ? darkPalette : lightPalette;

    return createTheme({
      palette: selectedPalette,
      typography: {
        fontFamily: '"Roboto", sans-serif, "Helvetica Neue", sans-serif',
        h4: { fontWeight: 600, fontSize: '2.125rem' },
        h5: { fontWeight: 500, fontSize: '1.5rem' },
        h6: { fontWeight: 500, fontSize: '1.25rem' },
        button: { textTransform: 'none', fontWeight: 500 },
      },
      components: { 
        MuiButton: {
          styleOverrides: {
            root: ({ theme }) => ({
              ...(theme.palette.mode === 'dark' && {
                color: '#deddff',
                lineHeight: '1',
                borderRadius: '8px',
                borderColor: 'rgba(255, 255, 255, 0.15)',
                padding: '12px 24px',
                backgroundColor: 'transparent',
                boxShadow: 'rgba(255, 255, 255, 0.2) 0px 0px 20px 0px inset',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.4)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  boxShadow: 'rgba(255, 255, 255, 0.3) 0px 0px 30px 0px inset',
                },
                '&:active': {
                  boxShadow: 'rgba(255, 255, 255, 0.3) 0px 0px 30px 0px inset',
                },
                // Destructive affordance must stay red — the glassy transparent
                // treatment above would otherwise null contained-error buttons
                // (delete confirms). Keep error-contained solid in dark mode.
                '&.MuiButton-containedError': {
                  backgroundColor: theme.palette.error.main,
                  color: theme.palette.error.contrastText,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: theme.palette.error.dark,
                    boxShadow: 'none',
                  },
                  // P7 tier-3 fix (P0-1, 2026-09-07): without this carve-out a
                  // DISABLED error-contained button (e.g. the P7 live primary
                  // before the acknowledgement is ticked) renders pixel-identical
                  // to the enabled one — the whole real-money gate becomes
                  // visually absent in dark mode. Applies theme-wide (delete
                  // confirms too), not just the pricing dialog.
                  '&.Mui-disabled': {
                    backgroundColor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                    boxShadow: 'none',
                  },
                },
              })
            })
          },
        },
        MuiPaper: {
          defaultProps: { 
            elevation: activePaletteMode === 'dark' ? 2 : 1,
            // Removido variant="outlined" globalmente para permitir mais flexibilidade
          }, 
          styleOverrides: { 
            root: ({ theme }) => ({
                borderRadius: '12px',
            })
          }
        },
        MuiCard: {
          styleOverrides: {
            root: ({theme}) => ({
              borderRadius: '16px',
              padding: '24px',
              background: 'transparent',
              backdropFilter: 'blur(34px)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255, 255, 255, 0.15)'
                : '1px solid rgba(0, 0, 0, 0.08)',
              boxShadow: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.2) 0px 0px 20px 0px inset'
                : '0 2px 12px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.3) 0px 0px 30px 0px inset'
                  : '0 4px 20px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
              }
            })
          }
        },
        MuiDialog: {
          defaultProps: {
            slotProps: {
              backdrop: {
                sx: (theme) => ({
                  backgroundColor: alpha(theme.palette.background.default, 0.8),
                  backdropFilter: 'blur(20px)',
                })
              }
            }
          },
          styleOverrides: {
            paper: ({theme, ownerState}) => ({
              backgroundImage: 'none !important',
              background: alpha(theme.palette.background.default, 0.8),
              backdropFilter: 'blur(34px)',
              borderRadius: '16px',
              padding: '24px',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255, 255, 255, 0.15)'
                : '1px solid rgba(0, 0, 0, 0.08)',
              boxShadow: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.2) 0px 0px 20px 0px inset'
                : '0 2px 12px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)',
            })
          }
        },
        MuiTable: {
          styleOverrides: {
            root: ({theme}) => ({
              borderCollapse: 'separate',
              borderRadius: '10px',
              overflow: 'hidden',
            })
          }
        },
        MuiTableCell: {
          styleOverrides: {
            head: ({theme}) => ({
              fontWeight: 'bold',
              backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey?.[100] || '#f5f5f5' : 'transparent',
              color: theme.palette.text.primary, // IMPORTANTE: Garante que a cor do texto seja do tema
            })
          }
        },
        MuiTableHead: {
          styleOverrides: {
            root: ({theme}) => ({
              backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey?.[100] || '#f5f5f5' : '#3D375B',
            })
          }
        },
        MuiDialogTitle: {
            styleOverrides: {
              root: ({theme}) => ({
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px',
                padding: '0 0 8px',
                borderBottom: `1px solid ${theme.palette.divider}`,
              })
            }
        },
        MuiDialogActions: {
            styleOverrides: { root: ({theme}) => ({ padding: '16px 24px', borderTop: `1px solid ${theme.palette.divider}` }) }
        },
        MuiAppBar: {
            styleOverrides: {
                root: ({theme}) => ({
                    // Usa a cor primária do tema atual, que já varia entre light/dark
                    backgroundColor: theme.palette.primary.main, 
                    color: theme.palette.primary.contrastText,
                })
            }
        },
        MuiDrawer: { // Estilo para o Paper dentro do Drawer
            styleOverrides: {
                paper: ({theme}) => ({
                    backgroundColor: theme.palette.background.default, // Garante que o Drawer use o fundo do tema
                    boxShadow:'none'
                })
            }
        },
        MuiListItemButton: {
          styleOverrides: {
            root: ({ theme }) => ({
              '&.Mui-selected': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(11, 87, 208, 0.75)' : '#d3e3fd',
                color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)',
                fontWeight: 'bold'
              },
              '&:hover': {
                backgroundColor: theme.palette.sidebar.listMenuItem.hoverBackground,
              },
            }),
          },
        },
        MuiAccordion: {
          styleOverrides: {
            root: () => ({
              backgroundColor: 'transparent',
              backgroundImage: 'none',
            }),
          },
        },
        // Pricing review B-1 (2026-09-04): MUI's outlined+warning Chip inherits
        // `warning.main` on the label from the root, 3.11:1 on the light paper
        // surface — under WCAG AA for small text. Border/root color (and the
        // delete icon) stay `warning.main`; only the label reads in
        // `text.secondary` (7.46:1). Dark mode is unchanged (7.84:1 already).
        MuiChip: {
          styleOverrides: {
            label: ({ theme, ownerState }) => ({
              ...(ownerState.variant === 'outlined' && ownerState.color === 'warning' && theme.palette.mode === 'light' && {
                color: theme.palette.text.secondary,
              }),
            }),
          },
        },
      }
    });
  }, [activePaletteMode, userChoiceMode, systemPrefersDark]); // Adicionado userChoiceMode e systemPrefersDark para garantir que o console.log capture os valores corretos no momento da criação do tema.

  // Listener para mudança de preferência do sistema
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      // Se userChoiceMode é 'system', o useMemo de activePaletteMode já deve pegar a mudança
      // e acionar a recriação do muiTheme.
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [userChoiceMode]);

  const themeContextValue = useMemo(() => ({
    setThemeModeSetting: setUserChoiceMode,
    modeSetting: userChoiceMode,
    activePaletteMode: activePaletteMode,
  }), [userChoiceMode, activePaletteMode]);

  return (
    <ThemeModeContext.Provider value={themeContextValue}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline enableColorScheme />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {children}
        </LocalizationProvider>
      </MuiThemeProvider>
    </ThemeModeContext.Provider>
  );
};